import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, DollarSign, Calendar } from 'lucide-react';
import Navigation from '../components/Navigation';
import api from '../services/api';

export default function Dashboard({ onLogout }) {
  const [summary, setSummary] = useState({
    total_income: 0,
    total_expense: 0,
    balance: 0,
    total_transactions: 0
  });
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [summaryData, transactionsData, profileData] = await Promise.all([
        api.getSummary(),
        api.getTransactions(),
        api.getProfile()
      ]);

      if (summaryData.success) {
        setSummary(summaryData.summary);
      }

      if (transactionsData.success) {
        setRecentTransactions(transactionsData.transactions.slice(0, 5));
      }

      if (profileData.success) {
        setUser(profileData.user);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50 to-slate-100">
        <Navigation onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-slate-300 border-t-amber-500 rounded-full animate-spin"></div>
            <p className="mt-4 text-slate-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50 to-slate-100">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;900&family=Work+Sans:wght@300;400;500;600&display=swap');
        * { font-family: 'Work Sans', sans-serif; }
        .font-display { font-family: 'Playfair Display', serif; }
        .stat-card {
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
        }
      `}</style>

      <Navigation onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="font-display text-4xl font-bold text-slate-900 mb-2">
            Welcome back, {user?.full_name || 'User'}!
          </h2>
          <p className="text-slate-600 text-lg">Here's your financial overview</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="stat-card bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-6 shadow-lg">
            <div className="text-emerald-100 text-sm font-medium tracking-wide mb-2">TOTAL INCOME</div>
            <div className="font-display text-3xl font-bold text-white">
              ₦{summary.total_income.toFixed(2)}
            </div>
          </div>

          <div className="stat-card bg-gradient-to-br from-rose-500 to-rose-600 rounded-2xl p-6 shadow-lg">
            <div className="text-rose-100 text-sm font-medium tracking-wide mb-2">TOTAL EXPENSES</div>
            <div className="font-display text-3xl font-bold text-white">
              ₦{summary.total_expense.toFixed(2)}
            </div>
          </div>

          <div className="stat-card bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-6 shadow-lg">
            <div className="text-amber-100 text-sm font-medium tracking-wide mb-2">BALANCE</div>
            <div className="font-display text-3xl font-bold text-white">
              ₦{summary.balance.toFixed(2)}
            </div>
          </div>

          <div className="stat-card bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 shadow-lg">
            <div className="text-blue-100 text-sm font-medium tracking-wide mb-2">TRANSACTIONS</div>
            <div className="font-display text-3xl font-bold text-white">
              {summary.total_transactions}
            </div>
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-200">
          <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-amber-50">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-display text-2xl font-bold text-slate-900">Recent Transactions</h3>
                <p className="text-slate-600 text-sm mt-1">Your latest financial activities</p>
              </div>
              <Link
                to="/transactions"
                className="bg-slate-900 text-white px-4 py-2 rounded-lg font-semibold hover:bg-slate-800 transition-all"
              >
                View All
              </Link>
            </div>
          </div>

          <div className="divide-y divide-slate-100">
            {recentTransactions.length === 0 ? (
              <div className="p-12 text-center">
                <DollarSign className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 font-medium">No transactions yet</p>
                <p className="text-slate-400 text-sm mt-1">Start tracking your finances</p>
                <Link
                  to="/transactions"
                  className="inline-block mt-4 bg-amber-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-amber-600 transition-all"
                >
                  Add Transaction
                </Link>
              </div>
            ) : (
              recentTransactions.map((transaction) => (
                <div key={transaction.id} className="p-5 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-slate-900">{transaction.description}</h4>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ₦{
                          transaction.type === 'income' 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : 'bg-rose-100 text-rose-700'
                        }`}>
                          {transaction.category}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(transaction.transaction_date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                    <div className={`font-display text-2xl font-bold ₦{
                      transaction.type === 'income' ? 'text-emerald-600' : 'text-rose-600'
                    }`}>
                      {transaction.type === 'income' ? '+' : '-'}₦{parseFloat(transaction.amount).toFixed(2)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Link
            to="/transactions"
            className="bg-white border-2 border-slate-200 rounded-2xl p-6 hover:border-amber-500 hover:shadow-lg transition-all group"
          >
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-emerald-200 transition-colors">
              <TrendingUp className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="font-display text-xl font-bold text-slate-900 mb-2">Add Transaction</h3>
            <p className="text-slate-600 text-sm">Record a new income or expense</p>
          </Link>

          <Link
            to="/profile"
            className="bg-white border-2 border-slate-200 rounded-2xl p-6 hover:border-amber-500 hover:shadow-lg transition-all group"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h3 className="font-display text-xl font-bold text-slate-900 mb-2">Update Profile</h3>
            <p className="text-slate-600 text-sm">Manage your account settings</p>
          </Link>

          <Link
            to="/transactions"
            className="bg-white border-2 border-slate-200 rounded-2xl p-6 hover:border-amber-500 hover:shadow-lg transition-all group"
          >
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-amber-200 transition-colors">
              <TrendingDown className="w-6 h-6 text-amber-600" />
            </div>
            <h3 className="font-display text-xl font-bold text-slate-900 mb-2">View Reports</h3>
            <p className="text-slate-600 text-sm">Analyze your spending patterns</p>
          </Link>
        </div>
      </div>
    </div>
  );
}
